import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { SlotController } from '../../controllers/slot-controller';
import componentStyles from './sic-container.css.lit';
import sicBackgrounds from '../sic-background/sic-background.css.lit';

export enum backgroundColors {
  PrimaryLightest = 'primary-lightest',
  PrimaryDefault = 'primary-default',
  SecondaryDefault = 'secondary-default',
  SecondaryLight = 'secondary-light',
  None = 'none',
}

export type backgroundColor = `${backgroundColors}`;

export type containerWidths = 'normal' | 'wide' | 'medium' | 'narrow' | 'full';

export type spacingOptions = 'normal' | 'none';

/**
 * The SicContainer component
 * @element sic-container
 *
 * @slot - default
 * @attr top-margin
 * @attr bottom-margin
 * @attr top-padding
 * @attr bottom-padding
 * @attr x-padding
 * @attr justify-end
 * @attr container-width
 * @attr background-color
 * @attr gap
 */
@customElement('sic-container')
export class SicContainer extends OutlineElement {
  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );
  static styles: CSSResultGroup = [componentStyles, sicBackgrounds];
  /**
   * Adds top margin.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'top-margin',
  })
  topMargin: spacingOptions = 'none';

  /**
   * Adds bottom margin.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'bottom-margin',
  })
  bottomMargin: spacingOptions = 'none';

  /**
   * Adds top padding.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'top-padding',
  })
  topPadding: spacingOptions = 'normal';

  /**
   * Adds bottom padding.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'bottom-padding',
  })
  bottomPadding: spacingOptions = 'normal';

  /**
   * Adds left and right padding.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'x-padding',
  })
  xPadding: spacingOptions = 'normal';

  /**
   * Adds top padding.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'print-top-padding',
  })
  printTopPadding: spacingOptions = 'normal';

  /**
   * Adds top padding.
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'print-bottom-padding',
  })
  printBottomPadding: spacingOptions = 'normal';

  /**
   * Positions content to the right.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'justify-end',
  })
  justifyEnd = false;

  /**
   * The container max-width
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'container-width',
  })
  containerWidth: containerWidths = 'normal';

  /**
   * CSS var to handle content on darker backgrounds.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: backgroundColor = 'none';

  /**
   * Sets space between items in container.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'gap',
  })
  gap = '0px';

  /**
   * Sets space between items in container.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'position-unset',
  })
  positionUnset = false;

  updated() {
    // Set css vars from properties.
    this.setVars();
  }

  /**
   * Set css vars without injecting an inline <style>.
   */
  setVars() {
    let theme = 'theme-dark';
    if (
      this.backgroundColor === 'primary-default' ||
      this.backgroundColor === 'secondary-default' ||
      this.backgroundColor === 'secondary-light'
    ) {
      theme = 'theme-light';
    }
    /* container variable values are set in outline.theme.css */
    this.style.setProperty(
      '--top-margin',
      `var(--container-mt-${this.topMargin})`
    );
    this.style.setProperty(
      '--bottom-margin',
      `var(--container-mb-${this.bottomMargin})`
    );
    this.style.setProperty(
      '--top-padding',
      `var(--container-pt-${this.topPadding})`
    );
    this.style.setProperty(
      '--bottom-padding',
      `var(--container-pb-${this.bottomPadding})`
    );
    this.style.setProperty(
      '--x-padding',
      `var(--container-px-${this.xPadding})`
    );
    this.style.setProperty('--sic-container-space-between-elements', this.gap);
    this.style.setProperty('--theme', theme);
  }

  render(): TemplateResult {
    const containerWidth = this.containerWidth;

    const wrapperClasses = {
      'container-wrapper': true,
      [containerWidth]: true,
      [`sic-bg--${this.backgroundColor}`]: true,
      'position-unset': this.positionUnset,
    };
    const containerClasses = {
      'container': true,
      [containerWidth]: true,
      'justifyEnd': this.justifyEnd,
      'position-unset': this.positionUnset,
    };
    return html`
      <div class="${classMap(wrapperClasses)}">
        <div class=${classMap(containerClasses)}>
          <slot></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-container': SicContainer;
  }
}
